import React from 'react';
import { get } from 'lodash';
import { Navigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { FormattedValue } from '../store/formatted-value';
import { ChartBox } from '../molecules/chart-box';
import { Group } from '../organisms/group';
import { SwitchBox } from '../molecules/switch-box';
import { ValueBox } from '../molecules/value-box';
import { Typography } from '@mui/material';
import { useSelector } from '../store/utils';
import { HeatPump } from '../atoms/icons/heat-pump';
import { ElectricalGrid } from '../atoms/icons/electrical-grid';
import { House } from '../atoms/icons/house';
import { Boiler } from '../atoms/icons/boiler';
import { SliderBox } from '../molecules/slider-box';
import { ParentPaper } from '../atoms/parent-paper';
import { FormattedHeatPumpStatus } from '../atoms/formatted-heat-pump-status';
import { SettingsHpSpotFlexibility } from '../templates/settings/boxes/settings-hp-spot-flexibility';

const HeatPump1 = () => {
  const [hpEnabled, ecooneHP] = useSelector(
    (s) =>
      [
        get(s.variables, 'HP_ENABLED'),
        get(s.variables, 'HP_TYPE') === 1,
      ] as const
  );
  const hpAcond = useSelector((s) => get(s.variables, 'HP_TYPE') == 4);
  const hpNibe = useSelector((s) => get(s.variables, 'HP_TYPE') == 2);
  if (!hpEnabled) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <Group layout="list">
        <SwitchBox
          title={
            <FormattedMessage
              id="heatpump.status"
              defaultMessage="Režim tepelka"
            />
          }
          subtitle={<FormattedHeatPumpStatus />}
          fullWidth
        />
      </Group>
      <Group
        layout="list"
        title={
          <FormattedMessage
            id="heatpump.statistics"
            defaultMessage="Statistiky"
          />
        }
      >
        <ChartBox variableId="HP_CONSUMED_GRAPH" />
      </Group>

      <Group
        title={
          <FormattedMessage
            id="heat_pump.common"
            defaultMessage="Obecné informace"
          />
        }
        layout="list"
      >
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="heat_pump.common.temp"
              defaultMessage="Výstupní teplota tepelného čerpadla"
            />
          }
          value={
            <FormattedValue
              variableId="HP_TEMP"
              opts={{
                style: 'unit',
                unit: 'celsius',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<HeatPump />}
        />

        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="heat_pump.common.power"
              defaultMessage="Aktuální odběr"
            />
          }
          value={
            <FormattedValue
              variableId="HP_ACTUAL_POWER"
              opts={{
                style: 'unit',
                unit: 'kilowatt',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<ElectricalGrid />}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="heat_pump.common.total"
              defaultMessage="Celková spotřeba"
            />
          }
          value={
            <FormattedValue
              variableId="HP_ENERGY_CONSUMED_TOTAL"
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<ElectricalGrid />}
          expanded={
            <Typography variant="body1" sx={{ color: 'text.disabled', mt: 1 }}>
              <FormattedMessage
                id="heat_pump.common.power_today"
                defaultMessage="Dnešní spotřeba:"
              />
              &nbsp;
              <FormattedValue
                variableId="STATS_HP_ENERGY_CONSUMED_TODAY"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
              <br />
              <FormattedMessage
                id="heat_pump.common.power_year"
                defaultMessage="Roční spotřeba:"
              />
              &nbsp;
              <FormattedValue
                variableId="STATS_HP_ENERGY_CONSUMED_YEAR"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
            </Typography>
          }
        />
      </Group>
      <Group
        title={
          <FormattedMessage id="heat_pump.room" defaultMessage="Vytápění" />
        }
        layout="list"
      >
        <ParentPaper>
          {!hpAcond && (
            <SwitchBox
              title={
                <FormattedMessage
                  id="heat_pump.room.on"
                  defaultMessage="Vytápění"
                />
              }
              switchVariableId="HP_ROOM_ON"
              fullWidth
              sx={{ mr: 2 }}
            />
          )}
          {(ecooneHP || hpAcond) && (
            <SwitchBox
              title={
                <FormattedMessage
                  id="heat_pump.room_cool.on"
                  defaultMessage="Chlazení"
                />
              }
              switchVariableId="HP_ROOM_COOL_ON"
              visibility={{ variableId: 'HP_FLEXI_COOL' }}
              fullWidth
              sx={{ mr: 2 }}
            />
          )}
          <ValueBox
            type="row"
            title={
              <FormattedMessage
                id="heat_pump.room.temp"
                defaultMessage="Aktuální teplota místnosti"
              />
            }
            value={
              <FormattedValue
                variableId="HP_ROOM_TEMP"
                opts={{
                  style: 'unit',
                  unit: 'celsius',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
            }
            visibility={{ variableId: 'HP_ROOM_TEMP_V' }}
            icon={<House />}
          />
          {!hpAcond && (
            <SliderBox
              title={
                <FormattedMessage
                  id="heat_pump.room.tempmin"
                  defaultMessage="Požadovaná teplota útlum"
                />
              }
              variableId="HP_ROOM_SET_TEMP_MIN"
              visibility={{ variableId: 'HP_ROOM_TEMP_V' }}
              opts={{
                style: 'unit',
                unit: 'celsius',
                maximumFractionDigits: 1,
              }}
              step={0.5}
              min={10}
              max={35}
            />
          )}
          {!hpAcond && (
            <SliderBox
              title={
                <FormattedMessage
                  id="heat_pump.room.tempmax"
                  defaultMessage="Požadovaná teplota komfort"
                />
              }
              variableId="HP_ROOM_SET_TEMP_MAX"
              visibility={{ variableId: 'HP_ROOM_TEMP_V' }}
              opts={{
                style: 'unit',
                unit: 'celsius',
                maximumFractionDigits: 1,
              }}
              step={0.5}
              min={10}
              max={35}
            />
          )}
          {hpAcond && (
            <SliderBox
              title={
                <FormattedMessage
                  id="heat_pump.room.tem"
                  defaultMessage="Požadovaná teplota"
                />
              }
              variableId="HP_ROOM_SET_TEMP_MIN"
              visibility={{ variableId: 'HP_ROOM_TEMP_V' }}
              opts={{
                style: 'unit',
                unit: 'celsius',
                maximumFractionDigits: 1,
              }}
              step={0.5}
              min={10}
              max={35}
            />
          )}
          <SliderBox
            title={
              <FormattedMessage
                id="heat_pump.room.flexitemp"
                defaultMessage="Flexibilní teplota"
              />
            }
            variableId="HP_ROOM_SET_FLEXI_TEMP"
            visibility={{ variableId: 'HP_FLEXI_ROOM' }}
            subtitle={
              <FormattedMessage
                id="heat_pump.room.flexitemp.subtitle"
                defaultMessage="Teplota o kterou je možné předehřát nebo předchladit dům"
              />
            }
            opts={{
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 1,
            }}
            step={0.2}
            min={0}
            max={10}
          />
          {ecooneHP && (
            <SliderBox
              title={
                <FormattedMessage
                  id="heat_pump.room.equithermalCorrection"
                  defaultMessage="Posun topné křivky"
                />
              }
              variableId="HP_EQUITHERMAL_CORRECTION"
              opts={{
                style: 'unit',
                unit: 'celsius',
                maximumFractionDigits: 1,
              }}
              step={0.5}
              min={-10}
              max={10}
            />
          )}
        </ParentPaper>
      </Group>
      <Group
        title={
          <FormattedMessage
            id="heat_pump.HW"
            defaultMessage="Ohřev teplé vody"
          />
        }
        visibility={{ variableId: 'HP_HW_TEMP_V' }}
        layout="list"
      >
        <ParentPaper>
          {!hpAcond && (
            <SwitchBox
              title={
                <FormattedMessage
                  id="heat_pump.HW.on"
                  defaultMessage="Ohřev teplé vody"
                />
              }
              switchVariableId="HP_HW_ON"
              visibility={{ variableId: 'HP_HW_TEMP_V' }}
              fullWidth
              sx={{ mr: 2 }}
            />
          )}
          <ValueBox
            type="row"
            title={
              <FormattedMessage
                id="heat_pump.HW.actual_temp"
                defaultMessage="Aktuální teplota nádrže"
              />
            }
            value={
              <FormattedValue
                variableId="HP_HW_TEMP"
                opts={{
                  style: 'unit',
                  unit: 'celsius',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
            }
            visibility={{ variableId: 'HP_HW_TEMP_V' }}
            icon={<Boiler />}
          />
          <SliderBox
            title={
              <FormattedMessage
                id="heat_pump.HW.temp"
                defaultMessage="Požadovaná teplota nádrže"
              />
            }
            variableId="HP_HW_SET_TEMP"
            opts={{
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 1,
            }}
            step={0.5}
            min={10}
            max={60}
          />
          {hpNibe && (
            <SliderBox
              title={
                <FormattedMessage
                  id="heat_pump.HW.hysteresis"
                  defaultMessage="Hystereze ohřevu teplé vody"
                />
              }
              variableId="HP_HW_TEMP_HYST"
              opts={{
                style: 'unit',
                unit: 'celsius',
                maximumFractionDigits: 1,
              }}
              step={1}
              min={5}
              max={15}
            />
          )}
          <SliderBox
            title={
              <FormattedMessage
                id="heat_pump.HW.flexitemp"
                defaultMessage="Flexibilní teplota"
              />
            }
            variableId="HP_HW_SET_FLEXI_TEMP"
            visibility={{ variableId: 'HP_FLEXI_HW' }}
            subtitle={
              <FormattedMessage
                id="heat_pump.HW.flexitemp.subtitle"
                defaultMessage="Teplota o kterou je možné předehřát teplou vodu"
              />
            }
            opts={{
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 1,
            }}
            step={0.2}
            min={0}
            max={20}
          />
        </ParentPaper>
      </Group>
      <Group
        title={
          <FormattedMessage
            id="heat_pump.flexi"
            defaultMessage="Flexibilita energie"
          />
        }
        layout="list"
        visibility={{ variableId: 'PV_INSTALL' }}
      >
        <SwitchBox
          title={
            <FormattedMessage
              id="heat_pump.flexi.flexi_on"
              defaultMessage="Režim flexibility "
            />
          }
          switchVariableId="HP_FLEXIBILITY_ON"
          subtitle={
            <FormattedMessage
              id="heat_pump.flexi.flexi_on.subtitle"
              defaultMessage="Režim aktivuje předehřev z přebytkové energie"
            />
          }
          fullWidth
        />
        <ValueBox
          type="row"
          visibility={{ variableId: 'HP_FLEXIBILITY_ON' }}
          title={
            <FormattedMessage
              id="heat_pump.flexi.pvstability"
              defaultMessage="Potenciál aktivace kompresoru"
            />
          }
          value={
            <FormattedValue
              variableId="HP_PV_STABILITY"
              opts={{
                style: 'percent',
                maximumFractionDigits: 0,
              }}
            />
          }
        />
      </Group>
      <Group
        title={
          <FormattedMessage
            id="heat_pump.flexi_sp"
            defaultMessage="Flexibilita spot"
          />
        }
        layout="list"
        visibility={{ variableId: 'SP_INSTALL' }}
      >
        <SettingsHpSpotFlexibility />
      </Group>
      <Group
        title={
          <FormattedMessage
            id="heat_pump.blocked"
            defaultMessage="Blokace kompresoru"
          />
        }
        layout="list"
        visibility={{
          variableIds: ['PV_BATTERY', 'HP_ECOONE'],
        }}
      >
        <SliderBox
          title={
            <FormattedMessage
              id="heat_pump.blocked.min_soc"
              defaultMessage="Blokace kompresoru při nedostupné síti"
            />
          }
          subtitle={
            <FormattedMessage
              id="heat_pump.blocked.subtitle"
              defaultMessage="Nabití baterie, od kterého dojde k blokaci kompresoru při výpadku sítě"
            />
          }
          variableId="HP_SOC_STOP"
          max={100}
          opts={{
            style: 'percent',
            maximumFractionDigits: 0,
          }}
        />
        <SliderBox
          title={
            <FormattedMessage
              id="heat_pump.blocked.min_soc_utility"
              defaultMessage="Blokace kompresoru při dostupné síti"
            />
          }
          subtitle={
            <FormattedMessage
              id="heat_pump.blocked.subtitle"
              defaultMessage="Nabití baterie, od kterého dojde k blokaci kompresoru při dostupné síti"
            />
          }
          variableId="HP_SOC_STOP_UTILITY"
          max={100}
          opts={{
            style: 'percent',
            maximumFractionDigits: 0,
          }}
        />
      </Group>
    </>
  );
};

export default HeatPump1;
